// src/components/PlayableGameCard.js
import React from "react";
import {
  FaLink,
  FaChrome,
  FaAndroid,
  FaApple,
  FaDesktop,
} from "react-icons/fa";
import Badge from "./Badge";
import Button from "./Button";

const PLATFORM_ICON_MAP = {
  web: FaChrome,
  android: FaAndroid,
  ios: FaApple,
  pc: FaDesktop,
};

export default function PlayableGameCard({ game, onPlay, onOpen }) {
  const {
    image,
    title,
    description,
    genre,
    isWeb3,
    platforms = [],
    tags = [],
    source,
    webglPath, 
  } = game;

  const canPlayHere = !!webglPath && typeof onPlay === "function";

  return (
    <div
      className="
        relative                         /* ⭐ so we can absolutely-position Play */
  w-full max-w-[320px]          /* ⬅️ Apply max width on mobile */
  sm:max-w-none                 /* ⬅️ Remove limit on sm+ screens */
        w-full flex rounded-2xl overflow-hidden
        border border-slate-800 bg-slate-900/70
        shadow-md shadow-slate-900/40
        hover:border-emerald-400/60 hover:shadow-emerald-500/20
        transition
        h-28 sm:h-32 md:h-36           /* 🔹 smaller on mobile */
      "
    >
      {/* IMAGE (clickable to open detail) */}
      <button
        type="button"
        onClick={onOpen}
        className="
          flex-shrink-0
          w-28 sm:w-32 md:w-40 lg:w-56   /* 🔹 narrower on mobile, same big size on desktop */
          h-full
          relative overflow-hidden
          bg-slate-800
          text-left
        "
      >
        {image ? (
          <img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover scale-[1.15]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-slate-500">
            No image
          </div>
        )}
      </button>

      {/* CONTENT (also clickable except for Play button) */}
      <div
        className="
          flex-1
          p-1.5 sm:p-2 md:p-3         /* 🔹 tighter padding on mobile */
          flex flex-col gap-1 min-w-0 cursor-pointer
        "
        onClick={onOpen}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-xs sm:text-sm md:text-base font-semibold text-slate-50 truncate">
              {title}
            </h3>
            <p className="text-[9px] sm:text-[10px] md:text-[11px] text-slate-400">
              {source}
            </p>
          </div>
          <Badge>{genre}</Badge>
        </div>

        {/* description – single line, with a bit of space underneath */}
        <p className="text-[10px] sm:text-[11px] md:text-sm text-slate-300 line-clamp-1 mb-0.5 sm:mb-1">
          {description}
        </p>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 text-[9px] sm:text-[10px]">
            {tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-full bg-slate-800 text-slate-200"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ⭐ OVERLAID PLAY BUTTON – does not affect layout */}
      {canPlayHere && (
        <div
          className="
            pointer-events-none
            absolute bottom-2 right-2
            flex items-end
          "
        >
          <div
            className="pointer-events-auto"
            onClick={(e) => {
              e.stopPropagation();
              onPlay && onPlay();
            }}
          >
            <Button
              className="
                bg-emerald-500 text-white hover:bg-emerald-400
                text-xs px-3 py-2              /* 🔹 smaller on mobile */
                sm:text-sm sm:px-4 sm:py-2      /* normal on small+ */
                md:px-5 md:py-3                 /* bigger on desktop */
                rounded-xl
                shadow-lg shadow-emerald-500/40
              "
            >
              Play Now
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
